'use client'

import { memo, useId, useMemo } from 'react'
import { useReducedMotion } from 'framer-motion'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'

/** baby: One cleanup beat: messy speech in, polished line out */
type RibbonBeat = {
  id: string
  raw: string
  clean: string
  badge: string
}

const BEATS: readonly RibbonBeat[] = [
  {
    id: 'umm',
    raw: 'uhm so it kind of feels chaotic, like nobody really knows what\'s going on…',
    clean: '…feels like nobody really knows what\'s going on.',
    badge: 'Removed Umm',
  },
  {
    id: 'like',
    raw: 'like can you tell the team the uh the new build is ready…',
    clean: 'Can you tell the team the new build is ready?',
    badge: 'Dropped fillers',
  },
  {
    id: 'yeah',
    raw: 'yeah so just download it again and ping me if anything breaks…',
    clean: 'Download it again and ping me if anything breaks.',
    badge: 'Cleaned phrasing',
  },
  {
    id: 'think',
    raw: 'I think the installer is fixed so uh we should ship today…',
    clean: 'The installer is fixed: we should ship today.',
    badge: 'Tightened copy',
  },
] as const

/**
 * baby: Exact ride path: concave wave matching the product shot.
 * Shared by the black ribbon, textPath, and the rider motion.
 */
const RIDE_PATH =
  'M40 128 C 180 210, 340 228, 500 172 C 660 116, 820 78, 980 96 C 1080 108, 1145 122, 1190 138'

const RAW_PATH =
  'M36 108 C 176 190, 336 208, 496 152 C 656 96, 816 58, 976 76 C 1076 88, 1141 102, 1186 118'

/** baby: Exact waveform bar heights from the cream pill in the shot */
const WAVE_BARS = [7, 14, 9, 18, 12, 22, 10, 17, 8, 15, 11, 19, 9] as const

const WaveformSvg = memo(function WaveformSvg({ live }: { live: boolean }) {
  return (
    <svg viewBox="0 0 56 28" className="nink-voice-wave-svg" aria-hidden>
      {WAVE_BARS.map((height, index) => {
        const x = 4 + index * 3.7
        const y = 14 - height / 2
        return (
          <rect
            key={index}
            className={cn('nink-voice-wave-bar', live && 'is-live')}
            x={x}
            y={y}
            width="2.1"
            height={height}
            rx="1.05"
            style={{ animationDelay: `${index * 0.06}s` }}
          />
        )
      })}
    </svg>
  )
})

/**
 * baby: Waveform capsule + badge, motion-bound to the wave path so it
 * literally rides the ribbon (SVG animateMotion = same coords as the stroke).
 */
const WaveRider = memo(function WaveRider({
  badge,
  live,
  reduced,
  motionPathId,
}: {
  badge: string
  live: boolean
  reduced: boolean
  motionPathId: string
}) {
  return (
    <g className="nink-voice-rider-g">
      {reduced ? (
        // baby: Park the rider near the head of the wave when motion is off
        <g transform="translate(210 168)">
          <RiderForeignObject badge={badge} live={live} />
        </g>
      ) : (
        <g>
          {/* baby: rotate=0 keeps the pill upright while it rides the curve */}
          <animateMotion
            dur="11s"
            repeatCount="indefinite"
            rotate="0"
            keyPoints="0.06;0.48;0.06"
            keyTimes="0;0.5;1"
            calcMode="spline"
            keySplines="0.37 0 0.63 1; 0.37 0 0.63 1"
          >
            <mpath href={`#${motionPathId}`} />
          </animateMotion>
          <RiderForeignObject badge={badge} live={live} />
        </g>
      )}
    </g>
  )
})

const RiderForeignObject = memo(function RiderForeignObject({
  badge,
  live,
}: {
  badge: string
  live: boolean
}) {
  return (
    <foreignObject x="-48" y="-86" width="96" height="120">
      <div className="nink-voice-rider-fo">
        <div className="nink-voice-badge">
          <span className="nink-voice-badge-icon" aria-hidden>
            <Check className="h-3.5 w-3.5" strokeWidth={3} />
          </span>
          <span>{badge}</span>
        </div>
        <div className={cn('nink-voice-processor', live && 'is-live')}>
          <span className="nink-voice-processor-ring" />
          <span className="nink-voice-processor-ring nink-voice-processor-ring-delay" />
          <div className="nink-voice-processor-capsule">
            <WaveformSvg live={live} />
          </div>
        </div>
      </div>
    </foreignObject>
  )
})

/**
 * baby: One wave segment: large ghost raw speech, thick black ride path
 * with large clean text, waveform pill riding the curve.
 */
const WaveUnit = memo(function WaveUnit({
  beat,
  pathId,
  live,
  reduced,
}: {
  beat: RibbonBeat
  pathId: string
  live: boolean
  reduced: boolean
}) {
  const cleanPathId = `${pathId}-clean`
  const rawPathId = `${pathId}-raw`

  return (
    <div className="nink-voice-unit">
      <svg
        className="nink-voice-ride-svg"
        viewBox="0 0 1220 280"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label={beat.clean}
      >
        <defs>
          <filter id={`${pathId}-soft`} x="-8%" y="-45%" width="116%" height="190%">
            <feDropShadow dx="0" dy="10" stdDeviation="12" floodColor="#000" floodOpacity="0.2" />
          </filter>
          <path id={cleanPathId} d={RIDE_PATH} />
          <path id={rawPathId} d={RAW_PATH} />
        </defs>

        {/* Thick black ribbon = the wave body */}
        <use
          href={`#${cleanPathId}`}
          fill="none"
          stroke="currentColor"
          className="nink-voice-curve-stroke"
          strokeWidth="64"
          strokeLinecap="round"
          filter={`url(#${pathId}-soft)`}
        />

        {/* Ghost raw speech on the parallel curve: larger type */}
        <text className="nink-voice-raw-path-text">
          <textPath href={`#${rawPathId}`} startOffset="1%">
            {beat.raw}
          </textPath>
        </text>

        {/* Clean typed copy inside the black wave: larger type */}
        <text className="nink-voice-curve-text">
          <textPath href={`#${cleanPathId}`} startOffset="16%">
            {beat.clean}
          </textPath>
        </text>

        <WaveRider
          badge={beat.badge}
          live={live}
          reduced={reduced}
          motionPathId={cleanPathId}
        />
      </svg>
    </div>
  )
})

/**
 * baby: Angled marquee of wave units: waveform pill rides the exact
 * curved ribbon; large path text stays readable while scrolling.
 */
export const NinkVoiceRibbon = memo(function NinkVoiceRibbon() {
  const reduceMotion = Boolean(useReducedMotion())
  const reactId = useId().replace(/:/g, '')

  const loopBeats = useMemo(
    () => (reduceMotion ? BEATS.slice(0, 1) : [...BEATS, ...BEATS]),
    [reduceMotion],
  )

  return (
    <div
      className="nink-voice-stage"
      aria-label={
        reduceMotion
          ? 'Voice cleaned into typed text'
          : 'Voice scrolling into cleaned text'
      }
    >
      <div className="nink-voice-atmosphere" aria-hidden>
        <span className="nink-voice-orb nink-voice-orb-a" />
        <span className="nink-voice-orb nink-voice-orb-b" />
        <span className="nink-voice-grain" />
      </div>

      <div className="nink-voice-angle">
        <div className="nink-voice-mask">
          <div
            className={cn(
              'nink-voice-track',
              reduceMotion ? 'is-static' : 'is-scroll-ltr',
            )}
          >
            {loopBeats.map((beat, index) => (
              <WaveUnit
                key={`${beat.id}-${index}`}
                beat={beat}
                pathId={`nink-ride-${reactId}-${beat.id}-${index}`}
                live={!reduceMotion}
                reduced={reduceMotion}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
})
